import { isEqual } from 'radash';

import { ComponentQuery, ConverterCategory, PortQuery } from '../types';

export type ConverterCategoryQuery = {
    value: ConverterCategory;
    matchesExtra?: (ports: PortQuery[], maxPorts?: number) => boolean;
    seedPorts: (primaryBase: PrimaryPortBase, restPorts: PortQuery[]) => PortQuery[];
    seedPortCount?: number;
    maxPorts?: number;
    excludePorts?: PortQuery[];
};

const DEFAULT_SEED_PORT_COUNT = 2;

const PORT_IDENTITY_FIELDS = ['voltageType', 'powerFlowDirection', 'purpose'] as const;

const portSatisfiesSeed = (port: PortQuery | undefined, seed: PortQuery): boolean =>
    !!port &&
    PORT_IDENTITY_FIELDS.every((field) => seed[field] === undefined || port[field] === seed[field]) &&
    (seed.controlMethods?.every((method) => port.controlMethods?.includes(method)) ?? true);

const matchesCategory = (def: ConverterCategoryQuery, ports: PortQuery[], maxPorts?: number): boolean => {
    if (def.maxPorts !== undefined && maxPorts !== def.maxPorts) {
        return false;
    }

    return (
        def.seedPorts(DEFAULT_PRIMARY_PORT_BASE, []).every((seed, index) => portSatisfiesSeed(ports[index], seed)) &&
        (def.matchesExtra?.(ports, maxPorts) ?? true)
    );
};

export const SOLID_STATE_TRANSFORMER_MIN_AC_VOLTAGE = 1000;

export type PrimaryPortBase = Omit<
    PortQuery,
    'powerFlowDirection' | 'purpose' | 'controlMethods' | 'strictDirection' | 'allowSharedMatch'
>;

export const DEFAULT_PRIMARY_PORT_BASE: PrimaryPortBase = { voltageType: 'DC' };

export const getPrimaryPortBase = (ports: PortQuery[] | null | undefined): PrimaryPortBase => {
    const {
        powerFlowDirection: _powerFlowDirection,
        purpose: _purpose,
        controlMethods: _controlMethods,
        strictDirection: _strictDirection,
        allowSharedMatch: _allowSharedMatch,
        ...primaryBase
    } = {
        ...DEFAULT_PRIMARY_PORT_BASE,
        ...ports?.[0],
    };
    return primaryBase;
};

export const isDefaultPort = (port: PortQuery): boolean => {
    return Object.keys(port).length === 0 || isEqual(port, DEFAULT_PRIMARY_PORT_BASE);
};

export const getRestPorts = (ports: PortQuery[] | null | undefined, maxPorts?: number): PortQuery[] => {
    const category = getConverterCategory(ports, maxPorts);
    const seedPortCount =
        CONVERTER_CATEGORY_QUERIES.find((def) => def.value === category)?.seedPortCount ?? DEFAULT_SEED_PORT_COUNT;
    return ports?.slice(seedPortCount) ?? [];
};

export const getPortsWithoutCategory = (ports: PortQuery[] | null | undefined, maxPorts?: number): PortQuery[] => {
    const primaryBase = getPrimaryPortBase(ports);
    const restPorts = getRestPorts(ports, maxPorts);

    if (isDefaultPort(primaryBase) && restPorts.length === 0) {
        return [];
    }

    return [primaryBase, ...restPorts];
};

export const CONVERTER_CATEGORY_QUERIES: ConverterCategoryQuery[] = [
    {
        value: ConverterCategory.SOLAR_INVERTER,
        seedPorts: (base, rest) => [
            {
                ...base,
                powerFlowDirection: 'input',
                purpose: 'solar',
                strictDirection: true,
            },
            {
                voltageType: 'AC',
                powerFlowDirection: 'output',
                strictDirection: true,
            },
            ...rest,
        ],
        excludePorts: [
            { voltageType: 'AC', powerFlowDirection: 'input' },
            { voltageType: 'AC', powerFlowDirection: 'bidirectional' },
            {
                voltageType: 'DC',
                powerFlowDirection: 'bidirectional',
                purpose: 'battery',
            },
        ],
    },
    {
        value: ConverterCategory.HYBRID_INVERTER,
        seedPortCount: 3,
        seedPorts: (base, rest) => [
            {
                ...base,
                powerFlowDirection: 'input',
                purpose: 'solar',
                strictDirection: true,
            },
            { voltageType: 'AC', powerFlowDirection: 'bidirectional' },
            {
                voltageType: 'DC',
                powerFlowDirection: 'bidirectional',
                purpose: 'battery',
            },
            ...rest,
        ],
    },
    {
        value: ConverterCategory.MPPT_TRACKER,
        seedPorts: (base, rest) => [
            {
                ...base,
                powerFlowDirection: 'input',
                controlMethods: ['maximum-power-point-tracking'],
            },
            {
                voltageType: 'DC',
                powerFlowDirection: 'output',
                strictDirection: true,
            },
            ...rest,
        ],
    },
    {
        value: ConverterCategory.RECTIFIER,
        matchesExtra: ([, p1]) => (p1?.voltage?.max ?? 0) < SOLID_STATE_TRANSFORMER_MIN_AC_VOLTAGE,
        seedPorts: (base, rest) => [
            { ...base, powerFlowDirection: 'output', strictDirection: true },
            {
                voltageType: 'AC',
                powerFlowDirection: 'input',
                strictDirection: true,
            },
            ...rest,
        ],
        excludePorts: [
            { voltageType: 'DC', powerFlowDirection: 'input' },
            { voltageType: 'DC', powerFlowDirection: 'bidirectional' },
            { voltageType: 'AC', maxCount: 1 },
            {
                voltageType: 'AC',
                voltage: { max: SOLID_STATE_TRANSFORMER_MIN_AC_VOLTAGE },
            },
        ],
    },
    {
        value: ConverterCategory.DC_DC_CONVERTER,
        seedPorts: (base, rest) => [
            { ...base, powerFlowDirection: 'bidirectional' },
            { voltageType: 'DC', powerFlowDirection: 'bidirectional' },
            ...rest,
        ],
        excludePorts: [{ purpose: 'solar' }, { voltageType: 'AC' }],
    },
    {
        value: ConverterCategory.PCS,
        seedPorts: (base, rest) => [
            { ...base, powerFlowDirection: 'bidirectional' },
            { voltageType: 'AC', powerFlowDirection: 'bidirectional' },
            ...rest,
        ],
    },
    {
        value: ConverterCategory.SOLID_STATE_TRANSFORMER,
        maxPorts: 2,
        matchesExtra: ([, p1]) => (p1?.voltage?.max ?? 0) >= SOLID_STATE_TRANSFORMER_MIN_AC_VOLTAGE,
        seedPorts: (base, rest) => [
            { ...base, powerFlowDirection: 'output' },
            {
                voltageType: 'AC',
                powerFlowDirection: 'input',
                voltage: { max: SOLID_STATE_TRANSFORMER_MIN_AC_VOLTAGE },
            },
            ...rest,
        ],
    },
];

export const getConverterCategory = (
    ports: PortQuery[] | null | undefined,
    maxPorts?: number,
): ConverterCategory | undefined => {
    return CONVERTER_CATEGORY_QUERIES.find((def) => matchesCategory(def, ports ?? [], maxPorts))?.value;
};

export const getConverterCategoriesPorts = (
    value: ConverterCategory,
    primaryBase: PrimaryPortBase,
    restPorts: PortQuery[] = [],
): PortQuery[] => {
    const { acConfiguration, ...primaryRest } = primaryBase;
    const dcPrimaryBase = { ...primaryRest, ...DEFAULT_PRIMARY_PORT_BASE };
    const seeded = CONVERTER_CATEGORY_QUERIES.find((def) => def.value === value)!.seedPorts(dcPrimaryBase, restPorts);

    if (!acConfiguration?.length) return seeded;
    const acIdx = seeded.findIndex((port) => port.voltageType === 'AC');
    if (acIdx === -1) return seeded;
    return seeded.map((port, index) => (index === acIdx ? { ...port, acConfiguration } : port));
};

export const getConverterCategoryDefaultQuery = (
    value: ConverterCategory,
): Pick<ComponentQuery, 'ports' | 'maxPorts' | 'excludePorts'> => {
    const preset = CONVERTER_CATEGORY_QUERIES.find((def) => def.value === value)!;
    return {
        ports: preset.seedPorts(DEFAULT_PRIMARY_PORT_BASE, []),
        maxPorts: preset.maxPorts,
        excludePorts: preset.excludePorts,
    };
};
